import React, { useMemo } from 'react';
import { Form, InputNumber, Select, Tag, Tooltip } from 'antd';
import type { SelectProps } from 'antd';
import type { DefaultOptionType } from 'antd/es/select';
import { useTranslation } from 'react-i18next';
import { SiteOptions } from '../hooks/useSiteOptions';
import {
    ATTEND_DAYS_MAX,
    ATTEND_DAYS_MIN,
    MAX_LEVELS,
    MAX_RULES,
    RuleDraft,
    RuleLevelDraft,
    ScopeConflict,
    createEmptyLevel,
    createEmptyRule,
    findScopeConflicts,
    keepOriginalSites,
} from '../utils/activityFormUtils';

export type RuleConfigMode = 'full' | 'limited';

interface RuleConfigTableProps {
    value?: RuleDraft[];
    onChange?: (rules: RuleDraft[]) => void;
    sites: SiteOptions;
    /** full：暂存 / 新建全可改；limited：未开始 / 进行中，只能在已有规则的下拉里新增站点 */
    mode?: RuleConfigMode;
    /** limited 模式下的原始规则，用来阻止删除已选站点 */
    originalRules?: RuleDraft[];
    /** 提交失败后把空的必填输入框标红 */
    showErrors?: boolean;
    disabled?: boolean;
    /** antd Form.Item 注入的字段 id，供 scrollToField 定位 */
    id?: string;
}

const isBlank = (v: number | null | undefined) => v === null || v === undefined;

/** 下拉框里露出的站点标签个数，超出折成「+N」 */
const MAX_VISIBLE_TAGS = 2;

/** maxTagCount 折叠出的「+N」占位也会走 tagRender：isMaxTag 为真、value 是 undefined，不能当站点 id */
export function isRestTag(props: { isMaxTag?: boolean; value?: unknown }): boolean {
    return !!props.isMaxTag || props.value === undefined || props.value === null;
}

/** 数字框宽度随内容增长：每个字符 1ch + 内边距，不小于各自最小宽度 */
export function numberInputWidth(value: number | null | undefined, minWidth: number): React.CSSProperties {
    const length = Math.max(String(value ?? '').length, 1);
    return { minWidth, width: `calc(${length}ch + 26px)` };
}

/** 冲突牵涉的站点类型：全是 Hub → "Hub"，全是 WHS → "WHS"，混合 → "Hub / WHS"，用于提示文案（选的是 Hub 不能提示"同一个 WHS"） */
export function describeConflictKind(conflict: ScopeConflict, whsHubMap: Record<number, number>): string {
    const hasWhs = conflict.siteIds.some((id) => id in whsHubMap);
    const hasHub = conflict.siteIds.some((id) => !(id in whsHubMap));
    if (hasHub && hasWhs) return 'Hub / WHS';
    return hasWhs ? 'WHS' : 'Hub';
}

/** 冲突描述：规则间 → 「规则 i 与 规则 j」；规则内 → 「规则 i」 */
export function describeConflictSites(conflict: ScopeConflict, siteNameMap: Record<number, string>, separator: string): string {
    return conflict.siteIds.map((id) => siteNameMap[id] ?? String(id)).join(separator);
}

/**
 * 奖励活动规则配置（原型 .mini.rules）：规则 N / 适用范围（Hub、WHS 两个多选下拉）/ 档位（最多 2 个）/ 删除。
 * 档位 2 的妥投门槛置灰并实时跟随档位 1；范围冲突整行底 #FFF4F3、chip 红边、表下红字提示。
 * 校验红框精确到控件（没填的那个才红）：用一层 noStyle + validateStatus="" 的 Form.Item 切断外层 Form.Item 的整体 error 状态下传。
 */
const RuleConfigTable: React.FC<RuleConfigTableProps> = ({
    value = [],
    onChange,
    sites,
    mode = 'full',
    originalRules = [],
    showErrors = false,
    disabled = false,
    id,
}) => {
    const { t } = useTranslation();
    const limited = mode === 'limited';
    const locked = disabled || limited;

    const conflicts = useMemo(() => findScopeConflicts(value, sites.whsHubMap), [value, sites.whsHubMap]);
    /** ruleIndex -> 冲突站点 id 集合 */
    const conflictSites = useMemo(() => {
        const map = new Map<number, Set<number>>();
        conflicts.forEach((c) => {
            [c.ruleIndex, c.otherRuleIndex].forEach((idx) => {
                if (idx === null) return;
                if (!map.has(idx)) map.set(idx, new Set());
                c.siteIds.forEach((id) => map.get(idx)!.add(id));
            });
        });
        return map;
    }, [conflicts]);

    const update = (next: RuleDraft[]) => onChange?.(next);

    const updateRule = (index: number, patch: Partial<RuleDraft>) => {
        update(value.map((rule, i) => (i === index ? { ...rule, ...patch } : rule)));
    };

    const handleSitesChange = (index: number, field: 'hubIds' | 'warehouseIds', next: number[]) => {
        const rule = value[index];
        const original = originalRules.find((r) => r.scopeId !== null && r.scopeId !== undefined && r.scopeId === rule.scopeId);
        const restored = limited && original ? keepOriginalSites(next, original[field]) : next;
        updateRule(index, { [field]: restored } as Partial<RuleDraft>);
    };

    const updateLevel = (index: number, levelIndex: number, patch: Partial<RuleLevelDraft>) => {
        const rule = value[index];
        const levels = rule.levels.map((level, li) => (li === levelIndex ? { ...level, ...patch } : level));
        // 一条规则只有一个妥投门槛：档位 2 跟随档位 1
        if (levelIndex === 0 && 'dailyDeliverQty' in patch && levels[1]) {
            levels[1] = { ...levels[1], dailyDeliverQty: patch.dailyDeliverQty ?? null };
        }
        updateRule(index, { levels });
    };

    const addLevel = (index: number) => {
        const rule = value[index];
        if (rule.levels.length >= MAX_LEVELS) return;
        updateRule(index, { levels: [...rule.levels, { ...createEmptyLevel(), dailyDeliverQty: rule.levels[0]?.dailyDeliverQty ?? null }] });
    };

    const removeLevel = (index: number) => {
        updateRule(index, { levels: value[index].levels.slice(0, 1) });
    };

    const addRule = () => {
        if (locked || value.length >= MAX_RULES) return;
        update([...value, createEmptyRule()]);
    };

    const removeRule = (index: number) => {
        if (locked) return;
        update(value.filter((_, i) => i !== index));
    };

    const tagRender =
        (ruleIndex: number, field: 'hubIds' | 'warehouseIds'): SelectProps['tagRender'] =>
        (props) => {
            if (isRestTag(props)) {
                return (
                    <Tag style={{ marginInlineEnd: 4, fontSize: 12 }} data-testid="site-rest-tag">
                        {props.label}
                    </Tag>
                );
            }
            const id = Number(props.value);
            const conflict = conflictSites.get(ruleIndex)?.has(id) ?? false;
            const rule = value[ruleIndex];
            const original = originalRules.find((r) => r.scopeId !== null && r.scopeId !== undefined && r.scopeId === rule?.scopeId);
            const fixed = limited && !!original && original[field].includes(id);
            // 回显时已选 id 不在下拉列表里（后来被拉黑）：用 id 原样显示灰色 chip，不丢数据
            const unknown = sites.siteNameMap[id] === undefined;
            return (
                <Tag
                    closable={props.closable && !fixed && !disabled}
                    onClose={props.onClose}
                    onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                    }}
                    title={unknown ? String(t('driverOps.activity.form.siteNotInList')) : undefined}
                    style={{
                        marginInlineEnd: 4,
                        fontSize: 12,
                        borderColor: conflict ? '#F0453A' : unknown ? '#BFBFBF' : '#D9D9D9',
                        color: conflict ? '#F0453A' : unknown ? '#8C8C8C' : '#1F1F1F',
                        background: conflict ? '#FFF4F3' : unknown ? '#F5F5F5' : '#fff',
                    }}
                >
                    {unknown ? String(id) : props.label}
                </Tag>
            );
        };

    const renderSiteRow = (ruleIndex: number, field: 'hubIds' | 'warehouseIds') => {
        const rule = value[ruleIndex];
        const isHub = field === 'hubIds';
        // Hub / WHS 任选其一：两个都空才红，选了任意一个两个都不红
        const scopeMissing = showErrors && rule.hubIds.length + rule.warehouseIds.length === 0;
        return (
            <div className="flex items-center gap-2 mb-[6px]">
                <span className="w-[34px] shrink-0 text-[12.5px] text-[#595959]">{isHub ? 'Hub' : 'WHS'}</span>
                <Select
                    mode="multiple"
                    size="small"
                    className="flex-1 min-w-0"
                    status={scopeMissing ? 'error' : undefined}
                    data-testid={`rule-${ruleIndex}-${field}`}
                    showSearch
                    optionFilterProp="label"
                    allowClear={!limited}
                    loading={sites.loading}
                    disabled={disabled}
                    options={(isHub ? sites.hubOptions : sites.whsGroupedOptions) as DefaultOptionType[]}
                    value={rule[field]}
                    placeholder={t(isHub ? 'driverOps.activity.form.searchHub' : 'driverOps.activity.form.searchWhs') as string}
                    onChange={(ids: number[]) => handleSitesChange(ruleIndex, field, ids.map(Number))}
                    tagRender={tagRender(ruleIndex, field)}
                    // 固定露出前 2 个站点名，其余折成「+N」（responsive 在聚焦搜索时会把全部标签折掉只剩 +N）
                    maxTagCount={MAX_VISIBLE_TAGS}
                    maxTagPlaceholder={(omitted) => (
                        <Tooltip title={omitted.map((o) => o.label).join(String(t('driverOps.activity.form.listSeparator')))}>
                            <span>+{omitted.length}</span>
                        </Tooltip>
                    )}
                    // 弹层挂在单元格内并固定朝下：页面滚动的是内层容器，挂 body 时视口下方不够高就会翻到上面；挂在格内则内容区随之变长，可以滚过去
                    getPopupContainer={(node) => node.parentElement ?? document.body}
                    placement="bottomLeft"
                    dropdownAlign={{ overflow: { adjustX: false, adjustY: false } }}
                />
            </div>
        );
    };

    const numberInput = (
        ruleIndex: number,
        levelIndex: number,
        field: keyof RuleLevelDraft,
        opts: { minWidth: number; min: number; max?: number; precision: number; disabled?: boolean; value?: number | null }
    ) => {
        const level = value[ruleIndex].levels[levelIndex];
        const raw = opts.value !== undefined ? opts.value : level[field];
        return (
            <InputNumber
                size="small"
                controls={false}
                style={{ ...numberInputWidth(raw, opts.minWidth), height: 28 }}
                min={opts.min}
                max={opts.max}
                precision={opts.precision}
                value={raw ?? null}
                disabled={locked || opts.disabled}
                status={showErrors && isBlank(raw) ? 'error' : undefined}
                onChange={(v) => updateLevel(ruleIndex, levelIndex, { [field]: v === null || v === undefined ? null : Number(v) })}
                data-testid={`rule-${ruleIndex}-level-${levelIndex}-${field}`}
            />
        );
    };

    const renderLevel = (ruleIndex: number, levelIndex: number) => {
        const rule = value[ruleIndex];
        const isSecond = levelIndex === 1;
        return (
            // 长语种（西语）放不下时只在元素之间换行，文字和输入框本身不拆；中文一行放得下，不会触发换行
            <div key={levelIndex} className="flex flex-wrap items-center gap-x-[6px] gap-y-[6px] text-[12.5px] text-[#595959] mb-[6px]">
                <b className="w-[46px] shrink-0 whitespace-nowrap text-[#1F1F1F]">{t('driverOps.activity.form.levelNo', { no: levelIndex + 1 })}</b>
                <span className="whitespace-nowrap">{t('driverOps.activity.form.deliverGte')}</span>
                {numberInput(ruleIndex, levelIndex, 'dailyDeliverQty', {
                    minWidth: 72,
                    min: 1,
                    precision: 0,
                    disabled: isSecond,
                    value: isSecond ? rule.levels[0]?.dailyDeliverQty ?? null : undefined,
                })}
                <span className="whitespace-nowrap">{t('driverOps.activity.form.perDay')}</span>
                <span className="w-[2px]" />
                <span className="whitespace-nowrap">{t('driverOps.activity.form.attend')}</span>
                {numberInput(ruleIndex, levelIndex, 'targetAttendDays', { minWidth: 56, min: ATTEND_DAYS_MIN, max: ATTEND_DAYS_MAX, precision: 0 })}
                <span className="whitespace-nowrap">{t('driverOps.activity.form.days')}</span>
                <span className="w-[2px]" />
                <span className="whitespace-nowrap">{t('driverOps.activity.form.rewardDollar')}</span>
                {numberInput(ruleIndex, levelIndex, 'rewardAmount', { minWidth: 96, min: 0.01, precision: 2 })}
            </div>
        );
    };

    const th = 'bg-[#FAFAFA] text-left font-semibold text-[13px] text-[#1F1F1F] px-[14px] py-[10px] border-b border-[#EFEFEF]';
    const td = 'px-2 py-[10px] align-top text-[13.5px] border-b border-[#EFEFEF]';

    return (
        <Form.Item noStyle validateStatus="">
            <div id={id}>
                {/* 固定列宽布局：表宽恒等于容器宽，长语种下档位列内容换行而不是把操作列顶出卡片 */}
                <table className="w-full table-fixed border-collapse">
                    <thead>
                        <tr>
                            <th className={th} style={{ width: 64 }}>{t('driverOps.activity.form.ruleColumn')}</th>
                            <th className={th} style={{ width: 300 }}>{t('driverOps.activity.form.scopeColumn')}</th>
                            <th className={th}>{t('driverOps.activity.form.levelColumn', { max: MAX_LEVELS })}</th>
                            <th className={th} style={{ width: 80 }}>{t('common.action')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {value.map((rule, index) => {
                            const hasConflict = conflictSites.has(index);
                            const selected = rule.hubIds.length + rule.warehouseIds.length;
                            return (
                                <tr key={rule.key} className={hasConflict ? 'bg-[#FFF4F3]' : ''} data-testid={`rule-row-${index}`}>
                                    <td className={td}>
                                        <b>{t('driverOps.activity.form.ruleNo', { no: index + 1 })}</b>
                                    </td>
                                    <td className={td}>
                                        {renderSiteRow(index, 'hubIds')}
                                        {renderSiteRow(index, 'warehouseIds')}
                                        <div className="text-[11.5px] text-[#8C8C8C] pl-[42px]">
                                            {t('driverOps.activity.form.selectedCount', { count: selected })}
                                        </div>
                                    </td>
                                    <td className={td}>
                                        {rule.levels.map((_, li) => renderLevel(index, li))}
                                        {rule.levels.length < MAX_LEVELS && !locked && (
                                            <div
                                                className="border border-dashed border-[#D9D9D9] rounded-[6px] h-[30px] flex items-center justify-center text-[#722ed1] text-[13px] cursor-pointer"
                                                onClick={() => addLevel(index)}
                                                data-testid={`rule-${index}-add-level-2`}
                                            >
                                                {t('driverOps.activity.form.addLevelTwo')}
                                            </div>
                                        )}
                                    </td>
                                    <td className={td}>
                                        {/* 第一行对齐档位 1：删除整条规则；第二行对齐档位 2：删除档位 2 */}
                                        <div className="h-[28px] mb-[6px] flex items-center whitespace-nowrap">
                                            {locked ? (
                                                <span className="text-[#BFBFBF]">—</span>
                                            ) : (
                                                <span className="text-[#722ed1] cursor-pointer" onClick={() => removeRule(index)} data-testid={`rule-${index}-delete`}>
                                                    {t('driverOps.activity.form.deleteRule')}
                                                </span>
                                            )}
                                        </div>
                                        {rule.levels.length > 1 && !locked && (
                                            <div className="h-[28px] mb-[6px] flex items-center whitespace-nowrap">
                                                <span
                                                    className="text-[#F0453A] cursor-pointer"
                                                    onClick={() => removeLevel(index)}
                                                    data-testid={`rule-${index}-remove-level-2`}
                                                >
                                                    {t('driverOps.activity.form.removeLevel')}
                                                </span>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {conflicts.map((conflict, i) => (
                    <div key={i} className="text-[12.5px] text-[#F0453A] mt-[6px]" data-testid="scope-conflict-hint">
                        {conflict.otherRuleIndex === null
                            ? t('driverOps.activity.validation.rules.conflictInRule', {
                                  ruleNo: conflict.ruleIndex + 1,
                                  sites: describeConflictSites(conflict, sites.siteNameMap, t('driverOps.activity.form.listSeparator')),
                              })
                            : t('driverOps.activity.validation.rules.conflictBetween', {
                                  ruleNo: conflict.ruleIndex + 1,
                                  otherRuleNo: conflict.otherRuleIndex + 1,
                                  sites: describeConflictSites(conflict, sites.siteNameMap, t('driverOps.activity.form.listSeparator')),
                                  kind: describeConflictKind(conflict, sites.whsHubMap),
                              })}
                    </div>
                ))}
                {!locked && (
                    <div
                        className={`border border-dashed rounded-[6px] h-[34px] flex items-center justify-center text-[13.5px] mt-[10px] ${
                            value.length >= MAX_RULES
                                ? 'border-[#D9D9D9] text-[#BFBFBF] cursor-not-allowed'
                                : 'border-[#722ed1] text-[#722ed1] cursor-pointer'
                        }`}
                        onClick={addRule}
                        data-testid="add-rule"
                    >
                        {t('driverOps.activity.form.addRule', { count: value.length, max: MAX_RULES })}
                    </div>
                )}
            </div>
        </Form.Item>
    );
};

export default RuleConfigTable;
